import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Textarea, toast, } from '@wit-support/ui';
import { Actions, FieldShell, SubmitButton, ZodField, ZodForm, useZodFormContext, CancelButton, } from '@wit-support/ui-forms';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { addComment, updateComment } from '../api.js';
/** Предел контракта `AddCommentRequest`/`UpdateCommentRequest` (`body` 1..4000). */
export const COMMENT_BODY_MAX_LENGTH = 4000;
/**
 * Русские тексты по коду сервера. Сервер отвечает машинным кодом
 * (`not_found`, `forbidden`), дословно в тосте он оператору ничего не говорит;
 * незнакомый код получает нейтральную фразу действия.
 */
const COMMENT_ERROR_RU = {
    invalid_body: 'Комментарий пустой или слишком длинный',
    not_found: 'Комментарий не найден',
    forbidden: 'Недостаточно прав',
};
export function commentErrorMessage(err, fallback) {
    const code = err instanceof ModuleRequestError ? err.code : null;
    return (code != null ? COMMENT_ERROR_RU[code] : undefined) ?? fallback;
}
/**
 * Та же форма, что у `AddCommentRequest` / `UpdateCommentRequest` контракта
 * (`body` 1..4000 после trim), но с РУССКИМИ сообщениями: текст ошибки под
 * полем читает оператор, а контракт говорит по-английски.
 */
export const CommentFormSchema = z.object({
    body: z
        .string()
        .trim()
        .min(1, 'Введите текст комментария')
        .max(COMMENT_BODY_MAX_LENGTH, `Не длиннее ${COMMENT_BODY_MAX_LENGTH} символов`),
});
/**
 * Поле тела комментария. Свой виджет вместо `TextareaWidget` кита ради двух
 * вещей, которых стандартный не умеет: автофокус (диалог открыт ради одного
 * поля — курсор должен быть уже в нём) и отправка по Cmd/Ctrl+Enter — тот же
 * жест, что в композере ответа, чтобы рука не переучивалась между попапами.
 * Отправка идёт через `form.requestSubmit()`, то есть через ту же валидацию,
 * что и кнопка «Сохранить».
 *
 * Жест защищён от повтора: удержанный Cmd+Enter (автоповтор клавиши) или
 * второе нажатие, пока запрос в пути, давали второй `requestSubmit()` и
 * второй `POST` — N одинаковых комментариев. Кнопка «Сохранить» на это время
 * и так отключена (`SubmitButton`), а поле — нет, поэтому гейт здесь: тот же
 * `isSubmitting`, что держит кнопку, плюс `e.repeat`.
 */
function CommentBodyWidget(props) {
    const { field, fieldState, meta, label, description, hint, disabled } = props;
    const { form } = useZodFormContext();
    const submitting = form.formState.isSubmitting;
    return (_jsx(FieldShell, { meta: meta, fieldState: fieldState, label: label, description: description, hint: hint, htmlFor: field.name, children: _jsx(Textarea, { ...field, id: field.name, value: field.value ?? '', placeholder: meta.placeholder, disabled: disabled, autoFocus: true, rows: 6, maxLength: COMMENT_BODY_MAX_LENGTH, onKeyDown: (e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    if (e.repeat || submitting)
                        return;
                    e.currentTarget.form?.requestSubmit();
                }
            } }) }));
}
/**
 * Один попап на создание и правку комментария — из рейла диалога и со
 * страницы «Комментарии». Сам ходит в API и сам инвалидирует кэш: и рейл
 * (`['conversation', id]` префиксом накрывает ленту комментариев и карточку
 * диалога с `commentCount`), и строку инбокса (`['conversations']`), и общую
 * ленту (`['comments']`) — чтобы у двух вызывающих сторон не разошлись
 * списки того, что обновлять.
 *
 * Успех НЕ тостится: вызывающая сторона видит результат сразу (комментарий
 * появился / текст поменялся), а тост «Сохранено» поверх этого — шум.
 */
export function CommentDialog(props) {
    const { open, onOpenChange, onSaved } = props;
    const queryClient = useQueryClient();
    const conversationId = props.mode === 'create' ? props.conversationId : props.comment.conversationId;
    const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['comments'] });
    };
    const mut = useMutation({
        mutationFn: (values) => props.mode === 'create'
            ? addComment(props.conversationId, values)
            : updateComment(props.comment.id, values),
        onSuccess: (saved) => {
            invalidate();
            onOpenChange(false);
            onSaved?.(saved);
        },
        onError: (err) => toast.error(commentErrorMessage(err, props.mode === 'create'
            ? 'Не удалось добавить комментарий'
            : 'Не удалось сохранить комментарий')),
    });
    const isEdit = props.mode === 'edit';
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { size: "md", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: isEdit ? 'Изменить комментарий' : 'Новый комментарий' }), _jsx(DialogDescription, { children: "\u0412\u043D\u0443\u0442\u0440\u0435\u043D\u043D\u044F\u044F \u0437\u0430\u043C\u0435\u0442\u043A\u0430 \u0434\u043B\u044F \u043A\u043E\u043C\u0430\u043D\u0434\u044B \u2014 \u043A\u043B\u0438\u0435\u043D\u0442 \u0435\u0451 \u043D\u0435 \u0443\u0432\u0438\u0434\u0438\u0442." })] }), _jsxs(ZodForm, { schema: CommentFormSchema, defaultValues: { body: isEdit ? props.comment.body : '' }, fieldMetaOverrides: { body: { placeholder: 'Заметка для команды…' } }, onSubmit: async (values) => {
                        // `mutateAsync` держит `isSubmitting` формы, пока запрос в пути;
                        // ошибка уже показана тостом в `onError`, здесь её только глотаем.
                        await mut.mutateAsync(values);
                    }, onServerError: () => { }, children: [_jsx(ZodField, { name: "body", label: "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439", as: CommentBodyWidget }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: () => onOpenChange(false) }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u0438\u0435\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] }, isEdit ? `edit:${props.comment.id}` : 'create')] }) }));
}
//# sourceMappingURL=comment-dialog.js.map